import { useEffect, useState } from "react";
import { api, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Pencil, Calculator, Receipt, History as HistoryIcon, FileSpreadsheet, FileDown } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const STATUSES = [
    { v: "pending", label: "Pending", c: "bg-amber-50 text-amber-700 border-amber-200" },
    { v: "invoiced", label: "Invoiced", c: "bg-blue-50 text-blue-700 border-blue-200" },
    { v: "paid", label: "Paid", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { v: "waived", label: "Waived", c: "bg-neutral-100 text-muted-foreground border-neutral-300" },
];
const STATUS_C = Object.fromEntries(STATUSES.map((s) => [s.v, s.c]));

const KINDS = [
    { v: "retainer", label: "Retainer" },
    { v: "milestone", label: "Milestone" },
    { v: "success_fee", label: "Success fee" },
    { v: "introducer", label: "Introducer fee" },
    { v: "other", label: "Other" },
];

export default function FeesTab({ deal }) {
    const [data, setData] = useState({ milestones: [], payments: [], summary: null });
    const [loading, setLoading] = useState(true);
    const [contacts, setContacts] = useState([]);

    const refresh = async () => {
        const [r, c] = await Promise.all([
            api.get(`/fees/deal/${deal.id}`),
            api.get(`/contacts?type=intermediary`).catch(() => ({ data: [] })),
        ]);
        setData(r.data);
        setContacts(c.data);
        setLoading(false);
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [deal.id]);

    if (loading) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;
    const s = data.summary || {};

    return (
        <div className="space-y-4 max-w-5xl" data-testid="fees-tab">
            {/* Summary tiles */}
            <div className="grid grid-cols-4 gap-3" data-testid="fees-summary">
                <Tile label="Pending" value={fmtMoney(s.by_status?.pending || 0, deal.currency)} />
                <Tile label="Invoiced" value={fmtMoney(s.by_status?.invoiced || 0, deal.currency)} accent="blue" />
                <Tile label="Paid" value={fmtMoney(s.by_status?.paid || 0, deal.currency)} accent="emerald" />
                <Tile label="Introducer due" value={fmtMoney(s.introducer_due_total || 0, deal.currency)} accent="gold" />
            </div>

            {/* Milestones */}
            <div className="border border-border rounded-md bg-white">
                <div className="px-6 py-4 flex items-center justify-between border-b border-border">
                    <div>
                        <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                            <Receipt className="w-4 h-4" /> Fee schedule
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Retainer + milestone payments. Status changes log to the audit trail.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <ExportPdfButton dealId={deal.id} dealName={deal.company_name} />
                        <LehmanDialog dealSize={deal.deal_size} currency={deal.currency} />
                        <MilestoneDialog dealId={deal.id} currency={deal.currency} contacts={contacts} onSaved={refresh} />
                    </div>
                </div>
                {data.milestones.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground" data-testid="fees-empty">
                        No fee milestones yet. Use <span className="text-foreground font-medium">+ Milestone</span> to start a schedule, or use the <span className="text-foreground font-medium">Lehman calculator</span> to suggest a success fee.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="milestones-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-6 py-3">Name</th>
                                <th className="text-left px-6 py-3">Kind</th>
                                <th className="text-left px-6 py-3">Due</th>
                                <th className="text-right px-6 py-3">Amount</th>
                                <th className="text-left px-6 py-3">Introducer</th>
                                <th className="text-left px-6 py-3">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence>
                                {data.milestones.map((m) => (
                                    <motion.tr
                                        key={m.id}
                                        layout
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        className="border-b border-border/60 last:border-0 hover:bg-muted/30"
                                        data-testid={`milestone-row-${m.id}`}
                                    >
                                        <td className="px-6 py-3 font-medium">{m.name}</td>
                                        <td className="px-6 py-3">
                                            <Badge variant="outline" className="text-[10px] uppercase font-mono">{m.kind}</Badge>
                                        </td>
                                        <td className="px-6 py-3 text-xs font-mono text-muted-foreground">
                                            {m.due_date ? new Date(m.due_date).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-6 py-3 text-right font-mono">{fmtMoney(m.amount, m.currency)}</td>
                                        <td className="px-6 py-3 text-xs">
                                            {m.introducer_split_pct ? (
                                                <span>
                                                    {m.introducer_split_pct}%
                                                    <span className="ml-1 text-muted-foreground font-mono">
                                                        ({fmtMoney(m.amount * m.introducer_split_pct / 100, m.currency)})
                                                    </span>
                                                </span>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-6 py-3">
                                            <StatusSelect milestone={m} onChanged={refresh} />
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <MilestoneDialog
                                                dealId={deal.id}
                                                currency={deal.currency}
                                                contacts={contacts}
                                                existing={m}
                                                onSaved={refresh}
                                            />
                                            <DeleteMilestoneBtn milestone={m} onDeleted={refresh} />
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>
                )}
            </div>

            {/* Payments log */}
            {data.payments.length > 0 && (
                <div className="border border-border rounded-md bg-white">
                    <div className="px-6 py-3 border-b border-border flex items-center gap-2">
                        <HistoryIcon className="w-3.5 h-3.5 text-gold" />
                        <h3 className="font-display font-bold tracking-tight text-sm">Payment events ({data.payments.length})</h3>
                    </div>
                    <ul className="divide-y divide-border/60" data-testid="payments-log">
                        {data.payments.slice(0, 10).map((p) => (
                            <li key={p.id} className="px-6 py-2.5 text-xs flex items-center gap-3">
                                <span className="font-mono text-[10px] text-muted-foreground w-32">{new Date(p.event_at).toLocaleString()}</span>
                                <Badge variant="outline" className="text-[10px] uppercase font-mono">{p.from_status} → {p.to_status}</Badge>
                                <span className="font-mono">{fmtMoney(p.amount, p.currency)}</span>
                                <span className="text-muted-foreground ml-auto">by {p.actor_name || "system"}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Tile({ label, value, accent }) {
    const colorMap = {
        blue: "border-blue-200 text-blue-700",
        emerald: "border-emerald-200 text-emerald-700",
        gold: "border-gold/40 text-gold",
    };
    const cls = colorMap[accent] || "";
    return (
        <div className={`border rounded-md p-4 bg-white ${cls || "border-border"}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`mt-1 font-mono text-lg font-bold ${cls ? "" : "text-navy"}`}>{value}</div>
        </div>
    );
}

function StatusSelect({ milestone, onChanged }) {
    const [val, setVal] = useState(milestone.status);
    const update = async (v) => {
        setVal(v);
        try {
            await api.patch(`/fees/${milestone.id}`, { status: v });
            toast.success(`Marked ${v}`);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
            setVal(milestone.status);
        }
    };
    return (
        <Select value={val} onValueChange={update}>
            <SelectTrigger className={`h-7 w-32 text-[10px] uppercase tracking-wider font-mono ${STATUS_C[val] || ""}`} data-testid={`milestone-status-${milestone.id}`}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function DeleteMilestoneBtn({ milestone, onDeleted }) {
    const remove = async () => {
        if (!confirm(`Delete milestone "${milestone.name}"?`)) return;
        await api.delete(`/fees/${milestone.id}`);
        toast.success("Removed");
        onDeleted();
    };
    return (
        <Button size="icon" variant="ghost" onClick={remove} data-testid={`delete-milestone-${milestone.id}`}>
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
    );
}

function MilestoneDialog({ dealId, currency, contacts, existing, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(() => ({
        name: existing?.name || "",
        amount: existing?.amount != null ? String(existing.amount) : "",
        kind: existing?.kind || "milestone",
        currency: existing?.currency || currency || "GBP",
        due_date: existing?.due_date || "",
        status: existing?.status || "pending",
        introducer_contact_id: existing?.introducer_contact_id || "",
        introducer_split_pct: existing?.introducer_split_pct != null ? String(existing.introducer_split_pct) : "",
        notes: existing?.notes || "",
    }));
    const [loading, setLoading] = useState(false);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Name required");
        if (!form.amount || isNaN(parseFloat(form.amount))) return toast.error("Amount required");
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                amount: parseFloat(form.amount),
                kind: form.kind,
                currency: form.currency,
                due_date: form.due_date || null,
                status: form.status,
                introducer_contact_id: form.introducer_contact_id || null,
                introducer_split_pct: form.introducer_split_pct === "" ? null : parseFloat(form.introducer_split_pct),
                notes: form.notes.trim() || null,
            };
            if (existing) await api.patch(`/fees/${existing.id}`, payload);
            else await api.post(`/fees/deal/${dealId}`, payload);
            toast.success(existing ? "Updated" : "Milestone added");
            setOpen(false);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {existing ? (
                    <Button size="icon" variant="ghost" data-testid={`edit-milestone-${existing.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                ) : (
                    <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white gap-1.5" data-testid="new-milestone-btn">
                        <Plus className="w-4 h-4" /> Milestone
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="milestone-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{existing ? "Edit milestone" : "Add fee milestone"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                        <Input required value={form.name} onChange={setF("name")} placeholder="e.g. NDA milestone" data-testid="milestone-name-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1.5 col-span-2">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount *</Label>
                            <Input required type="number" step="any" className="font-mono" value={form.amount} onChange={setF("amount")} data-testid="milestone-amount-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Currency</Label>
                            <Select value={form.currency} onValueChange={setF("currency")}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="GBP">GBP</SelectItem>
                                    <SelectItem value="USD">USD</SelectItem>
                                    <SelectItem value="EUR">EUR</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kind</Label>
                            <Select value={form.kind} onValueChange={setF("kind")}>
                                <SelectTrigger data-testid="milestone-kind-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Due date</Label>
                            <Input type="date" value={form.due_date} onChange={setF("due_date")} data-testid="milestone-due-date" />
                        </div>
                    </div>
                    <div className="pt-2 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wider text-gold font-medium mb-2">Introducer split (optional)</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Introducer</Label>
                                <Select value={form.introducer_contact_id || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, introducer_contact_id: v === "_none" ? "" : v }))}>
                                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">— None</SelectItem>
                                        {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Split %</Label>
                                <Input type="number" step="0.5" min="0" max="100" value={form.introducer_split_pct} onChange={setF("introducer_split_pct")} placeholder="e.g. 20" data-testid="milestone-split-pct" />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={setF("notes")} />
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="milestone-submit-btn">
                            {loading ? "Saving…" : existing ? "Save changes" : "Add milestone"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function LehmanDialog({ dealSize, currency }) {
    const [open, setOpen] = useState(false);
    const [size, setSize] = useState(dealSize ? String(dealSize) : "");
    const [cur, setCur] = useState(currency || "GBP");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const compute = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/fees/lehman/all?deal_size=${parseFloat(size)}&currency=${cur}`);
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && size && parseFloat(size) > 0) compute();
        // eslint-disable-next-line
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-1.5" data-testid="lehman-btn">
                    <Calculator className="w-3.5 h-3.5" /> Lehman calculator
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" data-testid="lehman-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-gold" /> Lehman fee calculator
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex items-end gap-3">
                        <div className="space-y-1.5 flex-1">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Deal size *</Label>
                            <Input
                                type="number"
                                className="font-mono"
                                value={size}
                                onChange={(e) => setSize(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && compute()}
                                data-testid="lehman-size-input"
                            />
                        </div>
                        <div className="space-y-1.5 w-24">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Currency</Label>
                            <Select value={cur} onValueChange={setCur}>
                                <SelectTrigger data-testid="lehman-currency-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="GBP">GBP</SelectItem>
                                    <SelectItem value="USD">USD</SelectItem>
                                    <SelectItem value="EUR">EUR</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={compute} disabled={loading || !size} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="lehman-compute-btn">
                            {loading ? "…" : "Compute"}
                        </Button>
                    </div>

                    {data && (
                        <div className="grid grid-cols-3 gap-4">
                            <VariantCard title="Standard" data={data.standard} currency={data.currency} testid="lehman-standard" />
                            <VariantCard title="Double" data={data.double} currency={data.currency} testid="lehman-double" />
                            <VariantCard title="Modified" data={data.modified} currency={data.currency} testid="lehman-modified" />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function VariantCard({ title, data, currency, testid }) {
    return (
        <div className="border border-border rounded-md p-3 space-y-2" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="font-display font-bold text-sm">{title}</div>
                {data.applicable ? (
                    <span className="font-mono text-[10px] text-muted-foreground">eff {data.effective_rate_pct}%</span>
                ) : (
                    <Badge variant="outline" className="text-[10px] uppercase">N/A</Badge>
                )}
            </div>
            <div className="font-mono text-lg font-bold text-gold">
                {data.applicable ? fmtMoney(data.total_fee, currency) : "—"}
            </div>
            {data.applicable ? (
                <table className="w-full text-[10px]">
                    <tbody>
                        {data.tiers.map((t, i) => (
                            <tr key={i} className="border-t border-border/40">
                                <td className="py-1 text-muted-foreground font-mono">{t.rate_pct}%</td>
                                <td className="py-1 text-right font-mono">{fmtMoney(t.tier_amount, currency)}</td>
                                <td className="py-1 text-right font-mono font-medium">{fmtMoney(t.fee, currency)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="text-[10px] text-muted-foreground italic">{data.reason}</div>
            )}
        </div>
    );
}


function ExportPdfButton({ dealId, dealName }) {
    const [loading, setLoading] = useState(false);
    const [variant, setVariant] = useState("standard");

    const download = async () => {
        setLoading(true);
        try {
            const resp = await api.get(`/fees/deal/${dealId}/export-pdf?variant=${variant}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            const safe = (dealName || "deal").replace(/[^a-z0-9_-]+/gi, "_").toLowerCase();
            a.download = `fee_schedule_${safe}_${variant}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Fee schedule exported");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Export failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <Select value={variant} onValueChange={setVariant}>
                <SelectTrigger className="h-8 w-36 text-xs" data-testid="pdf-variant-select">
                    <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="standard">Standard Lehman</SelectItem>
                    <SelectItem value="double">Double Lehman</SelectItem>
                    <SelectItem value="modified">Modified (2% flat)</SelectItem>
                </SelectContent>
            </Select>
            <Button
                variant="outline"
                onClick={download}
                disabled={loading}
                className="gap-1.5 h-8 text-xs"
                data-testid="export-fee-pdf-btn"
            >
                <FileDown className="w-3.5 h-3.5" />
                {loading ? "Generating…" : "Export fee schedule"}
            </Button>
        </div>
    );
}
